import { Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { BulkActionAvailabilityCheckResult } from '@app/shared/components/bulk-actions';
import { BulkActionAvailabilityMessages } from '@app/shared/components/bulk-actions/models/bulk-action-availability-messages';
import { BulkActionDialogEntry } from '@app/shared/components/bulk-actions/models/bulk-action-dialog-entry';
import { BulkActionDialogProcessingFunction } from '@app/shared/components/bulk-actions/models/bulk-action-dialog-processing-function';
import {
  BulkActionProcessingFailure,
  BulkActionProcessingResult,
  BulkActionProcessingResults,
} from '@app/shared/components/bulk-actions/models/bulk-action-dialog-result';
import { PageChangeEvent } from '@app/shared/components/pagination/page-change-event';
import { groupBy } from '@app/utils/common';
import { PortalError } from '@app/utils/error/portal-error';
import { forkJoinSerial } from '@app/utils/fork-join-serial';
import { PagedList } from '@app/utils/lists/paged-list/paged-list';
import { PagedListImpl } from '@app/utils/lists/paged-list/paged-list-impl';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

interface GroupedEntries<T> {
  alreadyAssigned: T[];
  available: T[];
  unavailable: T[];
}

@UntilDestroy()
@Component({
  selector: 'portal-bulk-action-entries',
  templateUrl: './bulk-action-entries.component.html',
  standalone: false,
})
export class BulkActionEntriesComponent<T> implements OnInit {
  @Input()
  availabilityChanged?: EventEmitter<void>;

  @Input()
  checkAvailability?: (entry: T) => BulkActionAvailabilityCheckResult;

  @Input()
  entries!: T[];

  @Input()
  entryTemplate!: TemplateRef<any>;

  @Input()
  label!: (entry: T) => string;

  @Input()
  messages?: BulkActionAvailabilityMessages;

  @Input()
  process!: BulkActionDialogProcessingFunction<T>;

  @Input()
  processInParallel!: boolean;

  @Output()
  change = new EventEmitter<BulkActionDialogEntry<T>[]>();

  @Output()
  submitting = new EventEmitter<void>();

  @Output()
  submitted = new EventEmitter<BulkActionProcessingResults>();

  list!: PagedList<BulkActionDialogEntry<T>>;

  allFailed: boolean = false;
  allSucceeded: boolean = false;
  errors: PortalError[] | null = null;
  hasBeenProcessed: boolean = false;
  hideAlreadyAssignedEntries: boolean = true;
  hideUnavailableEntries: boolean = true;
  pending: boolean = false;
  processing: number = 0;
  processingFailures: number = 0;
  processingSuccesses: number = 0;

  /**
   * The width of the failure progressbar (Angular won't show a string in the style attribute so we need to create a SafeStyle object)
   */
  progressFailure: SafeStyle;

  /**
   * The width of the success progressbar (Angular won't show a string in the style attribute so we need to create a SafeStyle object)
   */
  progressSuccess: SafeStyle;

  results: BulkActionProcessingResult[] | null = null;
  showOnlyFailures: boolean = false;

  alreadyAssignedEntries: T[] = [];
  unavailableEntries: T[] = [];

  get noEntryAvailable(): boolean {
    return this.list.filteredEntries.length === 0;
  }

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.list = new PagedListImpl({
      pageSize: 18,
    });

    this.availabilityChanged?.pipe(untilDestroyed(this)).subscribe(() => {
      this.runAvailabilityCheck();
    });

    this.runAvailabilityCheck();

    this.list.page$.pipe(untilDestroyed(this)).subscribe((page) => {
      this.change.emit(page.filteredEntries);
    });
  }

  private runAvailabilityCheck(): void {
    const { alreadyAssigned, available, unavailable } = this.groupEntriesByAvailability(this.entries);

    this.alreadyAssignedEntries = alreadyAssigned;
    this.unavailableEntries = unavailable;

    this.list
      .updateEntries(
        available.map((entry) => {
          return {
            processing: false,
            processed: false,
            value: entry,
          };
        })
      )
      .draw();
  }

  removeEntry(entry: BulkActionDialogEntry<T>): void {
    this.list.remove(entry).draw();
  }

  submit() {
    const entries = this.list.entries.filter((entry) => {
      const firstTry = this.results === null;
      const failedBefore = entry.success === false;

      return firstTry || failedBefore;
    });

    if (this.results != null) {
      this.toggleFailures(true);
    }

    this.errors = null;
    this.pending = true;
    this.processing = entries.length;
    this.processingFailures = 0;
    this.processingSuccesses = 0;

    this.submitting.emit();

    this.execute(entries).subscribe((results) => {
      this.allFailed = results.some((result) => result.success === true) === false;
      this.allSucceeded = results.some((result) => result.success === false) === false;
      this.pending = false;
      this.hasBeenProcessed = true;
      this.results = results;

      if (this.allSucceeded === false) {
        this.errors = results
          .filter((result) => result.success === false)
          .map((result: BulkActionProcessingFailure) => result.error);
      }

      this.submitted.emit({
        allFailed: this.allFailed,
        allSucceeded: this.allSucceeded,
        assertionFailed: false,
        errors: this.errors,
        results,
      });
    });
  }

  toggleFailures(force?: boolean): void {
    this.showOnlyFailures = force ?? !this.showOnlyFailures;

    if (this.showOnlyFailures) {
      this.list.updateFilteredEntries(this.list.entries.filter((entry) => entry.error != null)).draw();
    } else {
      this.list.updateFilteredEntries(this.list.entries).draw();
    }

    this.processingFailures = this.list.filteredEntries.filter((entry) => entry.error != null).length;
    this.processingSuccesses = this.list.filteredEntries.filter((entry) => entry.error == null).length;
    this.processing = this.list.filteredEntries.length;

    this.refreshProgress();
  }

  toggleAlreadyAssignedEntries(): void {
    this.hideAlreadyAssignedEntries = !this.hideAlreadyAssignedEntries;
  }

  toggleUnavailableEntries(): void {
    this.hideUnavailableEntries = !this.hideUnavailableEntries;
  }

  updatePage($event: PageChangeEvent): void {
    this.list.goTo($event.page).draw();
  }

  private execute(entries: BulkActionDialogEntry<T>[]) {
    let execution$: Observable<BulkActionProcessingResult[]>;

    const requests = entries.map((entry) => {
      entry.processing = true;

      return this.process(entry.value).pipe(
        map(() => {
          entry.error = null;
          entry.processing = false;
          entry.processed = true;
          entry.success = true;

          ++this.processingSuccesses;

          return {
            success: true,
          } as BulkActionProcessingResult;
        }),
        catchError((error: PortalError) => {
          entry.error = error;
          entry.processing = false;
          entry.processed = true;
          entry.success = false;

          ++this.processingFailures;

          return of({
            error: {
              ...error,
              i18n: this.label(entry.value),
            },
            success: false,
          } as BulkActionProcessingResult);
        }),
        tap(() => {
          this.refreshProgress();
        })
      );
    });

    if (this.processInParallel) {
      execution$ = forkJoin(requests);
    } else {
      execution$ = forkJoinSerial(requests);
    }

    return execution$;
  }

  private groupEntriesByAvailability(entries: T[]): GroupedEntries<T> {
    let grouped: GroupedEntries<T>;

    if (this.checkAvailability != null) {
      grouped = groupBy(entries, (entry) => this.checkAvailability(entry));
    } else {
      grouped = {
        alreadyAssigned: [],
        available: entries,
        unavailable: [],
      };
    }

    return {
      alreadyAssigned: grouped.alreadyAssigned ?? [],
      available: grouped.available ?? [],
      unavailable: grouped.unavailable ?? [],
    };
  }

  /**
   * Recalculate the current progress
   */
  private refreshProgress() {
    this.progressFailure = this.sanitizer.bypassSecurityTrustStyle(
      `width: ${(this.processingFailures / this.processing) * 100}%`
    );

    this.progressSuccess = this.sanitizer.bypassSecurityTrustStyle(
      `width: ${(this.processingSuccesses / this.processing) * 100}%`
    );
  }
}
